/* eslint-disable @typescript-eslint/no-explicit-any */
import "./tournament-game-picks.scss";
import "./group-play.scss";
import "./playoffs.scss";
import "./playoffs-six-rounds.scss";
import "./tournament-game-picks-content.scss";
import { type TournamentGamePicksFrontendProperties } from "../generated-types";
import { type Module } from "~/foundation/Bootstrapper/types";
import { pollService } from "../services/pollService";
import { pollQuizLeaderboardService } from "../services/pollQuizLeaderboardService";


export const TournamentGamePicks: Module<TournamentGamePicksFrontendProperties> = (container, props) => {
	const quizTitle = container.querySelector<HTMLHeadingElement>(".tournament-game-picks__title");
	const quizContentIntro = container.querySelector<HTMLDivElement>(".tournament-game-picks__intro");
	const quizContentEdit = container.querySelector<HTMLDivElement>(".tournament-game-picks__edit");
	const quizContentOutro = container.querySelector<HTMLDivElement>(".tournament-game-picks__outro");
	const quizContentFinished = container.querySelector<HTMLDivElement>(".tournament-game-picks__finished");
	const saveButton = container?.querySelector<HTMLButtonElement>(".tournament-game-picks__save");
	const quizPolls = container.querySelectorAll<HTMLDivElement>(".tournament-game-picks__group-play-item, .tournament-game-picks__playoff-poll");
	const quizAnswers = container.querySelectorAll<HTMLInputElement>(".tournament-game-picks__team input");
	const quizStats = container.querySelector<HTMLDivElement>(".tournament-game-picks__stats");
	const quizPoints = quizStats?.querySelector<HTMLDivElement>(".tournament-game-picks__points");
	const quizRank = quizStats?.querySelector<HTMLDivElement>(".tournament-game-picks__rank");
	const quizPlayoff = container.querySelector<HTMLElement>(".tournament-game-picks__playoff");
	const quizGroupPlay = container.querySelector<HTMLElement>(".tournament-game-picks__group-play");
	const viewAllButton = container.querySelector<HTMLButtonElement | HTMLAnchorElement>(".tournament-game-picks__view-all");

	// Playoff: when no quiz is current, show finished (if all polls ended) or outro; show last group on mobile (five- and six-round)
	if (props.isPlayoff && quizPlayoff) {
		const hasCurrent = quizPlayoff.querySelector(".tournament-game-picks__playoff-quiz--current");
		if (!hasCurrent) {
			quizPlayoff.classList.add("tournament-game-picks__playoff--no-current-quiz");

			const playoffPolls = container.querySelectorAll<HTMLElement>(".tournament-game-picks__playoff-poll");
			const allPollsEnded =
				playoffPolls.length > 0 &&
				Array.from(playoffPolls).every((p) => p.classList.contains("tournament-game-picks__playoff-poll--ended"));

			if (allPollsEnded && quizContentFinished) {
				// No current quiz and all polls ended: show finished content
				quizContentIntro?.classList.remove("content--active");
				quizContentEdit?.classList.remove("content--active");
				quizContentOutro?.classList.remove("content--active");
				quizContentFinished.classList.add("content--active");
			} else {
				// No current but not all ended: show outro
				quizContentIntro?.classList.remove("content--active");
				quizContentEdit?.classList.remove("content--active");
				quizContentOutro?.classList.add("content--active");
			}

			// Show last group on mobile when no current quiz (finished or outro)
			const lastGroup = quizPlayoff.querySelector<HTMLElement>(".tournament-game-picks__playoff-quiz:last-child");
			if (lastGroup) {
				lastGroup.classList.add("tournament-game-picks__playoff-quiz--current");
			}
		}
	}

	const initEvents = () => {

		if (quizStats && props.gameId) {
			pollQuizLeaderboardService.getPosition(props.gameId as string).then((result) => {
				const { success, data } = result as any;

				if (!success) {
					console.warn("error fetching game leaderboard position");
					return;
				}

				if (quizPoints) {
					const pointsLabel = quizPoints.getAttribute("data-points-label") ?? "";
					const points = data?.data?.totalPoints !== undefined ? data.data.totalPoints.toString() : "0";
					quizPoints.innerText = pointsLabel.replace("{0}", points);
				}
				if (quizRank) {
					const position = data?.data?.position;
					const rankingLabel = quizRank.getAttribute("data-ranking-label") ?? "";
					const noRankingLabel = quizRank.getAttribute("data-no-ranking-label") ?? "";
					if (position === 0 || position === undefined) {
						quizRank.innerText = noRankingLabel;
					} else {
						quizRank.innerText = rankingLabel.replace("{0}", String(position));
					}
				}
			}).catch(errors => {
				console.error((errors as any)?.message);
			});
		}

		// Handle checkbox changes to limit selection to 2 per quiz item (only for non-playoff mode)
		if (!props.isPlayoff) {
			quizPolls.forEach((quizItem) => {
				const itemCheckboxes = quizItem.querySelectorAll<HTMLInputElement>('.tournament-game-picks__team input[type="checkbox"]');

				itemCheckboxes.forEach(checkbox => {
					checkbox.addEventListener('change', () => {
						// Count how many are checked
						const checkedCount = Array.from(itemCheckboxes).filter(cb => cb.checked).length;

						// If 2 are checked, disable all unchecked checkboxes
						// If less than 2, enable all checkboxes
						itemCheckboxes.forEach(cb => {
							if (cb.checked) return; // Don't disable checked boxes
							cb.disabled = checkedCount >= 2;
						});
					});
				});
			});
		}

		// Save button label — {0} = groups/polls changed since last save (one per group-play-item or playoff-poll), {1} = total editable groups/polls; reset {0} on save
		const saveButtonLabelSpan = saveButton?.querySelector<HTMLElement>(".cta__label-animation");
		let saveButtonLabelTemplate: string | null = null;
		const changedPollsSinceSave = new Set<HTMLElement>();

		const updateSaveButtonLabel = () => {
			if (!saveButtonLabelSpan) return;
			if (!saveButtonLabelTemplate) {
				const fromDom = (saveButtonLabelSpan.textContent ?? "").trim();
				if (fromDom.includes("{0}") && fromDom.includes("{1}")) {
					saveButtonLabelTemplate = fromDom;
				} else {
					saveButtonLabelTemplate = props.isPlayoff ? "Save {0}/{1}" : "Save {0}/{1} groups";
				}
			}
			const changedCount = changedPollsSinceSave.size;
			let total: number;
			if (props.isPlayoff) {
				const editablePolls = new Set<HTMLElement>();
				quizAnswers.forEach((input) => {
					if (input.disabled) return;
					const poll = input.closest<HTMLElement>(".tournament-game-picks__playoff-poll");
					if (poll) editablePolls.add(poll);
				});
				total = editablePolls.size;
			} else {
				const editableGroups = new Set<HTMLElement>();
				quizAnswers.forEach((input) => {
					if (input.disabled) return;
					const groupItem = input.closest<HTMLElement>(".tournament-game-picks__group-play-item");
					if (groupItem) editableGroups.add(groupItem);
				});
				total = editableGroups.size;
			}
			saveButtonLabelSpan.textContent = saveButtonLabelTemplate.replace("{0}", String(changedCount)).replace("{1}", String(total));
		};

		updateSaveButtonLabel();

		// Show save button and update label when any input changes; track changed polls (so two inputs in same poll = 1 change)
		quizAnswers.forEach(input => {
			input.addEventListener('change', () => {
				const poll = input.closest<HTMLElement>('.tournament-game-picks__playoff-poll, .tournament-game-picks__group-play-item');
				if (poll) changedPollsSinceSave.add(poll);
				if (saveButton) {
					saveButton.removeAttribute('hidden');
					updateSaveButtonLabel();
				}
			});
		});

		// No start button — show edit content when user changes any input (intro → edit; outro/finished → edit again after save)
		if (quizContentEdit) {
			const showEditContent = () => {
				if (quizContentEdit.classList.contains("content--active")) return;
				quizContentIntro?.classList.remove("content--active");
				quizContentOutro?.classList.remove("content--active");
				quizContentFinished?.classList.remove("content--active");
				quizContentEdit.classList.add("content--active");
				quizTitle?.classList.add("hidden--sm");
			};
			quizAnswers.forEach(input => {
				input.addEventListener("change", showEditContent);
			});
		}

		// View all: replace {0} with poll count; show on small screens when >4 polls (playoff: current quiz; group: all groups)
		if (viewAllButton) {
			const collapseText = viewAllButton.getAttribute("data-alternate-label") ?? "Collapse";

			if (props.isPlayoff && quizPlayoff) {
				const currentQuiz = container.querySelector<HTMLElement>(".tournament-game-picks__playoff-quiz--current");
				const pollCount = currentQuiz?.querySelectorAll(".tournament-game-picks__playoff-poll").length ?? 0;
				const expandText = viewAllButton.textContent?.replace("{0}", String(pollCount)) ?? "";
				if (expandText) {
					viewAllButton.textContent = expandText;
				}

				const updateViewAllTextAndState = () => {
					const isExpanded = quizPlayoff.classList.contains("tournament-game-picks__playoff--expanded");
					viewAllButton.textContent = isExpanded ? collapseText : expandText;
					viewAllButton.setAttribute("aria-expanded", String(isExpanded));
				};

				const updateViewAllVisibility = () => {
					const currentQuizEl = container.querySelector<HTMLElement>(".tournament-game-picks__playoff-quiz--current");
					const pollsInCurrentQuiz = currentQuizEl?.querySelectorAll(".tournament-game-picks__playoff-poll").length ?? 0;
					const isSmallScreen = window.innerWidth < 1280;
					const showButton = isSmallScreen && pollsInCurrentQuiz > 4;
					if (showButton) {
						viewAllButton.removeAttribute("hidden");
						updateViewAllTextAndState();
					} else {
						viewAllButton.setAttribute("hidden", "");
					}
				};

				viewAllButton.setAttribute("aria-expanded", "false");

				viewAllButton.addEventListener("click", (e) => {
					e.preventDefault();
					const wasExpanded = quizPlayoff.classList.contains("tournament-game-picks__playoff--expanded");
					quizPlayoff.classList.toggle("tournament-game-picks__playoff--expanded");
					updateViewAllTextAndState();
					if (wasExpanded) {
						container.scrollIntoView({ behavior: "smooth", block: "start" });
					}
					setTimeout(() => {
						updateViewAllVisibility();
					}, 450);
				});

				window.addEventListener("resize", updateViewAllVisibility);
				requestAnimationFrame(() => updateViewAllVisibility());
			} else if (!props.isPlayoff && quizGroupPlay) {
				const pollCount = quizGroupPlay.querySelectorAll(".tournament-game-picks__group-play-item").length;
				const expandText = viewAllButton.textContent?.replace("{0}", String(pollCount)) ?? "";
				if (expandText) {
					viewAllButton.textContent = expandText;
				}

				const updateViewAllTextAndState = () => {
					const isExpanded = quizGroupPlay.classList.contains("tournament-game-picks__group-play--expanded");
					viewAllButton.textContent = isExpanded ? collapseText : expandText;
					viewAllButton.setAttribute("aria-expanded", String(isExpanded));
				};

				const updateViewAllVisibility = () => {
					const isMobile = window.innerWidth < 768;
					const showButton = isMobile && pollCount > 4;
					if (showButton) {
						viewAllButton.removeAttribute("hidden");
						updateViewAllTextAndState();
					} else {
						viewAllButton.setAttribute("hidden", "");
						quizGroupPlay.classList.remove("tournament-game-picks__group-play--expanded");
					}
				};

				viewAllButton.setAttribute("aria-expanded", "false");

				viewAllButton.addEventListener("click", (e) => {
					e.preventDefault();
					const wasExpanded = quizGroupPlay.classList.contains("tournament-game-picks__group-play--expanded");
					quizGroupPlay.classList.toggle("tournament-game-picks__group-play--expanded");
					updateViewAllTextAndState();
					if (wasExpanded) {
						container.scrollIntoView({ behavior: "smooth", block: "start" });
					}
					setTimeout(() => {
						updateViewAllVisibility();
					}, 450);
				});

				window.addEventListener("resize", updateViewAllVisibility);
				requestAnimationFrame(() => updateViewAllVisibility());
			}
		}

		// Handle save button click
		if (saveButton) {
			saveButton.addEventListener('click', (e) => {
				e.preventDefault();

				saveButton.setAttribute('hidden', '');

				// Collect all poll answers
				const pollSubmits: Array<{ pollId: string; answerIds: string[] }> = [];

				quizPolls.forEach((poll: HTMLDivElement) => {
					// Skip polls that have ended
					if (poll.classList.contains('tournament-game-picks__group-play-item--ended') || poll.classList.contains('tournament-game-picks__playoff-poll--ended')) return;

					const pollId = poll.dataset.pollId;
					if (!pollId) return;

					// Get all inputs in this poll
					const pollInputs = poll.querySelectorAll<HTMLInputElement>('.tournament-game-picks__team input');

					// Get all checked inputs in this poll
					const checkedInputs = poll.querySelectorAll<HTMLInputElement>('input:checked');

					// Mark teams as answered for inputs in this poll
					if (!props.isPlayoff) {
						pollInputs.forEach(input => {
							input.parentElement?.classList.toggle('tournament-game-picks__team--answered', input.checked);
						});
					}

					const answerIds = Array.from(checkedInputs)
						.map(input => input.getAttribute('data-answer-id'))
						.filter((id): id is string => id !== null);

					// Only add if there are checked answers
					if (answerIds.length > 0) {
						pollSubmits.push({ pollId, answerIds });
					}
				});

				// Submit all answers at once
				pollService.submitMultiplePollAnswers({ pollSubmits }).then((result) => {
					const { success, data } = result as any;

					if (!success) {
						console.warn("error submitting poll answers");
						return;
					}

					changedPollsSinceSave.clear();
					updateSaveButtonLabel();

					if (quizContentOutro && quizContentEdit) {
						quizContentIntro?.classList.remove("content--active");
						quizContentEdit.classList.remove("content--active");
						quizContentOutro.classList.add("content--active");
					}

					container.scrollIntoView({ behavior: "smooth", block: "start" });

					window.dataLayer.push({
						event: "multiplePollSubmit",
						gameId: props.gameId,
						pollTitle: quizTitle?.innerText,
						pollAnswer: pollSubmits.join(", "),
						pollGlobal: "false",
						pollAccess: data.isInternal ? "internal" : "external" // Internal or external
					});
				}).catch(errors => {
					console.error((errors as any)?.message);
				});
			});
		}
	};

	if (props.isAuthenticated) {
		initEvents();
	}
};

export default TournamentGamePicks;